import { injectable } from 'inversify';

const MAX_SCROLL_ATTEMPTS = 30;
const SCROLL_RETRY_DELAY = 100;
const HALF = 2;

@injectable()
export class EventTypeRowScrollService {
  scrollToRow(eventTypeId: string): () => void {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let prevScrollHeight = 0;

    const tryScroll = () => {
      const row = this.findRow(eventTypeId);
      const container = row ? this.getScrollableAncestors(row)[0] : undefined;

      const isStable =
        !!container && container.scrollHeight === prevScrollHeight;

      if (container) {
        prevScrollHeight = container.scrollHeight;
      }

      if ((!row || !container || !isStable) && attempts < MAX_SCROLL_ATTEMPTS) {
        attempts += 1;
        retryTimer = setTimeout(tryScroll, SCROLL_RETRY_DELAY);

        return;
      }

      if (row) {
        this.scrollRowIntoView(row);
      }
    };

    tryScroll();

    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }

  private findRow(eventTypeId: string): HTMLElement | null {
    const cell = document.querySelector<HTMLElement>(
      `[data-testid="datagrid-tablecell-render-${eventTypeId}"]`,
    );

    return cell?.closest('tr') ?? null;
  }

  private isScrollable(element: HTMLElement) {
    const { overflowY, overflow } = window.getComputedStyle(element);
    const canScroll = /(auto|scroll|overlay)/.test(`${overflowY} ${overflow}`);

    return canScroll && element.scrollHeight > element.clientHeight + 1;
  }

  private getScrollableAncestors(row: HTMLElement) {
    const containers: HTMLElement[] = [];
    let parent = row.parentElement;

    while (parent) {
      if (this.isScrollable(parent)) {
        containers.push(parent);
      }

      parent = parent.parentElement;
    }

    return containers;
  }

  private scrollRowIntoView(row: HTMLElement) {
    this.getScrollableAncestors(row)
      .reverse()
      .forEach((container) => {
        const containerRect = container.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const offset =
          rowRect.top -
          containerRect.top -
          container.clientHeight / HALF +
          rowRect.height / HALF;

        container.scrollTo({
          top: container.scrollTop + offset,
          behavior: 'smooth',
        });
      });
  }
}
